/**
 * THE RECALCULATION, SAID WHERE APPROVE IS (A2-1b-2).
 *
 * A heavy gesture's recalculation runs on the mc-worker and can take minutes. Before
 * this line the reviewer learned about it only from approve's 409; now the footer
 * says it while it happens: waiting to start, recalculating (for how long), or
 * failed with a way to try again. Nothing renders while idle — which is also what
 * every org whose recalculations still run in the API reads, so for them the footer
 * is unchanged.
 *
 * NO STEP NAME: a recalculation writes no per-step events (`RecomputeRecorder`), and
 * the pipeline's step names mean nothing to a compliance officer anyway (`ConeLine`).
 *
 * ONE RETRY: when the reviewer's retry fails too, the line says it keeps failing and
 * offers no button until the server's cooldown has passed (`retryable`).
 */
import { isPermissionDenied } from "@/hooks/useGuardedMutation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AlertTriangle, Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { getApiErrorCode, toastApiError } from "../../../../../api/apiError"
import {
  type RecomputeStatus,
  useRetryRecompute,
} from "../../../../../api/recomputeStatusApi"

/** Whole minutes since `since`, never negative; 0 for a missing time. */
export function minutesSince(since: string | null, now: number): number {
  if (!since) return 0
  const started = Date.parse(since)
  if (Number.isNaN(started)) return 0
  return Math.max(0, Math.floor((now - started) / 60_000))
}

/** Re-render every 30 s while a job is open, so the elapsed minutes move on their own. */
function useMinuteClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    setNow(Date.now())
    const id = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(id)
  }, [active])
  return now
}

export function RecomputeStatusLine({
  declId,
  status,
}: {
  declId: string
  status: RecomputeStatus | undefined
}) {
  const { translate } = useTranslation()
  const retry = useRetryRecompute()
  const open = status?.status === "queued" || status?.status === "running"
  const now = useMinuteClock(open)

  if (!status || status.status === "idle") return null

  const minutes = String(minutesSince(status.since, now))

  if (status.status === "failed") {
    return (
      <span
        className="inline-flex items-center gap-1.5"
        data-testid="recompute-status-line"
        data-status="failed"
      >
        <AlertTriangle size={12} aria-hidden="true" />
        {translate(
          status.retryable
            ? "MC_DECL_V2_RECOMPUTE_FAILED"
            : "MC_DECL_V2_RECOMPUTE_FAILED_AGAIN",
        )}
        {status.retryable && retry.canRun && (
          <button
            type="button"
            className="underline underline-offset-2"
            disabled={retry.isPending}
            onClick={() =>
              retry.mutate(declId, {
                // A 409 (NOTHING_TO_RETRY: already re-queued, cooling down, or
                // recalculated meanwhile) is not an error to the reviewer: the
                // status is asked again on settle and the line updates itself.
                onError: (error) => {
                  if (getApiErrorCode(error) === "NOTHING_TO_RETRY") return
                  if (isPermissionDenied(error)) return
                  toastApiError(
                    error,
                    translate,
                    "MC_DECL_V2_RECOMPUTE_RETRY_FAILED",
                  )
                },
              })
            }
          >
            {translate("MC_DECL_V2_RECOMPUTE_RETRY")}
          </button>
        )}
      </span>
    )
  }

  return (
    <span
      className="inline-flex items-center gap-1.5"
      data-testid="recompute-status-line"
      data-status={status.status}
    >
      <Loader2 size={12} className="animate-spin" aria-hidden="true" />
      {translate(
        // TUR-1725: the run's final review on the worker (a `review` job), said as
        // what it is, queued or walking alike: approve is waiting on it.
        status.kind === "review"
          ? "MC_DECL_V2_RECOMPUTE_REVIEW_RUNNING"
          : status.status === "running"
            ? "MC_DECL_V2_RECOMPUTE_RUNNING"
            : "MC_DECL_V2_RECOMPUTE_QUEUED",
        { minutes },
      )}
    </span>
  )
}
